import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, PackageX } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TransactionCard } from "@/components/waste/transaction-card";
import { WasteEventModals } from "@/components/waste/waste-event-modals";

import { useWasteEvent } from "@/hooks/use-waste-events";
import { useWasteEventActionsController } from "@/hooks/use-waste-event-actions-controller";
import { useSitesWithActiveMenus } from "@/hooks/use-active-menus";
import { useEnabledClassificationSites } from "@/hooks/use-enabled-classification-sites";
import { useClaims } from "@/hooks/use-claims";
import { useAuthStore } from "@/stores/auth";
import { useTranslation } from "@/i18n/use-translation";

const EMPTY_SITE_SET: ReadonlySet<string> = new Set();

/**
 * A single waste event at its own URL (POS2-849) — reachable from a comment /
 * mention notification, independent of the Transactions list's filters and
 * pagination. Renders the same TransactionCard and drives the same actions as
 * the list via the shared controller.
 */
export default function WasteEventDetailPage() {
  const { t } = useTranslation();
  const { id = null } = useParams();
  const eventQuery = useWasteEvent(id);
  const sitesQuery = useSitesWithActiveMenus();
  const enabledSitesQuery = useEnabledClassificationSites();
  const actions = useWasteEventActionsController();

  const { isPlatformAdmin, role } = useClaims();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const isModerator = isPlatformAdmin || role === "Org Admin";

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);
  const event = eventQuery.data ?? null;
  const site = event ? (sites.find((s) => s.id === event.site_id) ?? null) : null;
  const autoClassifyingSites = enabledSitesQuery.data ?? EMPTY_SITE_SET;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
        <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-gray-500">
          <Link to="/waste/transactions">
            <ArrowLeft className="size-3.5" aria-hidden />
            {t("waste.transactionsLink")}
          </Link>
        </Button>
        <h1 className="text-lg font-medium text-gray-900">{t("waste.eventTitle")}</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {eventQuery.isLoading && <DetailSkeleton />}
        {!eventQuery.isLoading && eventQuery.isError && <NotAvailable />}
        {event && (
          <div className="mx-auto max-w-md">
            <TransactionCard
              event={event}
              onAnalyse={actions.onAnalyse}
              onConfirm={actions.onConfirm}
              onRelabel={actions.setRelabelEvent}
              isConfirming={actions.isConfirming(event.id)}
              isAnalysing={actions.isAnalysing(event.id)}
              siteAutoClassifying={autoClassifyingSites.has(event.site_id)}
              onExpandImage={actions.setLightboxEvent}
              siteHasActiveMenu={site?.has_active_menu ?? false}
              onEdit={actions.setEditEvent}
              onOpenComments={actions.setCommentsEvent}
            />
          </div>
        )}
      </div>

      <WasteEventModals
        controller={actions}
        sites={sites}
        defaultSiteId={event?.site_id ?? null}
        defaultKitchenId={event?.kitchen_id ?? null}
        currentUserId={currentUserId}
        isModerator={isModerator}
      />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto flex max-w-md flex-col gap-3 rounded-lg border border-black/[0.06] bg-white p-4">
      <Skeleton className="aspect-[4/3] w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-3 w-1/3" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-3" />
        <Skeleton className="h-3" />
        <Skeleton className="h-3" />
        <Skeleton className="h-3" />
      </div>
    </div>
  );
}

function NotAvailable() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <PackageX className="size-10 text-gray-300" aria-hidden />
      <h3 className="mt-3 text-sm font-medium text-gray-900">{t("waste.eventUnavailable")}</h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500">{t("waste.eventUnavailableHint")}</p>
      <Button asChild variant="secondary" size="sm" className="mt-4">
        <Link to="/waste/transactions">{t("waste.backToTransactions")}</Link>
      </Button>
    </div>
  );
}
